import type { InviteCode } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { useInviteCodeActivations } from '../../api/inviteCodes.ts';
import { coerceErrorMessage } from '../../components/Toast.tsx';
import { CustomerCell } from '../../components/identity/CustomerCell.tsx';
import { formatCnyCents, formatLocalTimestamp } from '../../components/formatters.ts';
import {
  Button,
  Dialog,
  EmptyState,
  TBody,
  THead,
  Table,
  TableEmpty,
  TableSkeleton,
  Td,
  Th,
  Tr,
} from '../../ui/index.ts';

/**
 * Who one invite code brought in (#1985).
 *
 * Mounted only while open, so a closed dialog costs no request. The header
 * numbers come from two different places on purpose: `total` is the code's
 * lifetime headcount and only this endpoint knows it, while today's count
 * and the daily quota are already on the row the operator clicked — asking
 * for them again would let the two surfaces disagree on the same screen.
 *
 * The detail list is capped server-side; `total > activations.length` is
 * the only signal that the cap bit, and the footer says so rather than
 * letting the operator read a short list as the whole story.
 */
interface ActivationsDialogProps {
  readonly code: InviteCode;
  readonly onClose: () => void;
}

export function ActivationsDialog({ code, onClose }: ActivationsDialogProps): JSX.Element {
  const t = useT();
  const query = useInviteCodeActivations(code.id);

  const activations = query.data?.activations ?? [];
  const total = query.data?.total ?? 0;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('invite_codes.activations.title', { code: code.code })}
      closeLabel={t('common.close')}
      size="xl"
      testId="invite-codes-activations-dialog"
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {query.isSuccess ? (
          <p
            className="m-0 text-dense text-paper-low"
            data-testid="invite-codes-activations-summary"
          >
            {t('invite_codes.activations.summary', {
              total,
              today: code.today_activation_count,
              quota: code.effective_daily_quota,
            })}
          </p>
        ) : null}

        {query.isLoading ? <TableSkeleton rows={4} /> : null}

        {query.isError ? (
          <EmptyState
            title={t('invite_codes.activations.error')}
            hint={coerceErrorMessage(query.error)}
            action={<Button onClick={() => void query.refetch()}>{t('common.retry')}</Button>}
            testId="invite-codes-activations-error"
          />
        ) : null}

        {query.isSuccess ? (
          <Table testId="invite-codes-activations-table">
            <THead>
              <Tr interactive={false}>
                <Th>{t('invite_codes.activations.column.email')}</Th>
                <Th>{t('invite_codes.activations.column.org')}</Th>
                <Th>{t('invite_codes.activations.column.activated_at')}</Th>
                <Th num>{t('invite_codes.activations.column.grant')}</Th>
                <Th>{t('invite_codes.activations.column.grant_expires')}</Th>
              </Tr>
            </THead>
            <TBody>
              {activations.length === 0 ? (
                <TableEmpty colSpan={5} testId="invite-codes-activations-empty">
                  {t('invite_codes.activations.empty')}
                </TableEmpty>
              ) : null}
              {activations.map((activation) => (
                <Tr
                  key={activation.account_id}
                  data-testid={`invite-codes-activation-${activation.account_id}`}
                >
                  <Td>
                    <CustomerCell
                      orgId={activation.org_id}
                      secondary={<span>{activation.email}</span>}
                    />
                  </Td>
                  <Td title={activation.org_id}>{activation.org_name}</Td>
                  <Td>{formatLocalTimestamp(activation.activated_at)}</Td>
                  <Td num>{formatCnyCents(activation.grant_amount_cny_cents, false)}</Td>
                  <Td>{formatLocalTimestamp(activation.grant_expires_at)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ) : null}

        {query.isSuccess && total > activations.length ? (
          <p
            className="m-0 text-dense text-paper-low"
            data-testid="invite-codes-activations-truncated"
          >
            {/* The cap is the server's number, never restated here — the
                footer counts what it actually rendered, so a change to
                `ACTIVATIONS_PAGE_LIMIT` cannot turn this line into a lie. */}
            {t('invite_codes.activations.truncated', {
              shown: activations.length,
              total,
            })}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
